import { useRef, useState, useEffect, useCallback } from 'react';
import { useScrollReveal, getRevealStyle } from '@/hooks/useScrollReveal';
import { books2026 } from '@/mocks/books2026';
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from '../../../../../shared/book-publication-status';

function BookCard({ book }: { book: (typeof books2026)[0] }) {
  const publicationBadge = getBookPublicationBadge(book.publishDate);
  return (
    <div className="group relative w-[260px] md:w-[280px] flex-shrink-0">
      {/* Corner accent lines */}
      <div className="absolute -top-px -left-px w-4 h-px bg-primary-500/40" />
      <div className="absolute -top-px -left-px w-px h-4 bg-primary-500/40" />
      <div className="absolute -bottom-px -right-px w-4 h-px bg-primary-500/40" />
      <div className="absolute -bottom-px -right-px w-px h-4 bg-primary-500/40" />

      <div className="relative h-full border border-primary-500/10 bg-background-50/50 backdrop-blur-sm overflow-hidden transition-all duration-500 hover:border-primary-500/30 hover:shadow-[0_0_24px_rgba(0,0,0,0.3)] group-hover:shadow-primary-500/8">
        {/* Cover image */}
        <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-100">
          <img
            src={book.coverUrl}
            alt={`${book.title} 書籍封面`}
            title={`${book.title} 書籍封面`}
            className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
          />
          {publicationBadge && (
            <span className={`absolute top-2 right-2 z-10 rounded-full px-2 py-0.5 text-xs font-medium ${getBookPublicationBadgeClass(publicationBadge)}`}>
              {getBookPublicationBadgeLabel(publicationBadge)}
            </span>
          )}
          {/* Subtle overlay on hover */}
          <div className="absolute inset-0 bg-primary-500/0 group-hover:bg-primary-500/5 transition-colors duration-500" />

          {/* Category badge — only when category exists */}
          {book.category && (
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center px-2.5 py-1 text-[10px] font-medium tracking-wider uppercase font-label bg-background-950/60 text-foreground-100 backdrop-blur-sm border border-primary-500/20">
                {book.category}
              </span>
            </div>
          )}
        </div>

        {/* Info area */}
        <div className="p-5 md:p-6">
          {/* Full title as readable web text */}
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-100 leading-[1.3] tracking-tight">
            {book.title}
          </h3>
          {book.subtitle && (
            <p className="mt-1.5 text-sm text-foreground-300 leading-[1.5] line-clamp-2">
              {book.subtitle}
            </p>
          )}

          <div className="mt-4 pt-4 border-t border-primary-500/10">
            {/* Authors / contributors */}
            <p className="text-xs text-foreground-400 leading-[1.7] line-clamp-2">
              {book.authors}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] font-medium text-foreground-500 tracking-wider font-label uppercase">
                ISBN
              </span>
              <span className="text-xs text-foreground-300 font-mono tracking-wide">
                {book.isbn}
              </span>
            </div>

            {/* Book info entry point */}
            <div className="mt-3">
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
              >
                前往完整書目
                <i className="ri-arrow-right-up-line text-[10px]" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CatalogCard() {
  return (
    <div className="group relative w-[260px] md:w-[280px] flex-shrink-0">
      {/* Corner accent lines */}
      <div className="absolute -top-px -left-px w-4 h-px bg-primary-500/40" />
      <div className="absolute -top-px -left-px w-px h-4 bg-primary-500/40" />
      <div className="absolute -bottom-px -right-px w-4 h-px bg-primary-500/40" />
      <div className="absolute -bottom-px -right-px w-px h-4 bg-primary-500/40" />

      <div className="relative h-full border border-primary-500/10 bg-background-50/50 backdrop-blur-sm overflow-hidden transition-all duration-500 hover:border-primary-500/30 hover:shadow-[0_0_24px_rgba(0,0,0,0.3)] group-hover:shadow-primary-500/8 flex flex-col">
        {/* Visual area */}
        <div className="relative flex-1 min-h-[260px] overflow-hidden bg-background-100 flex flex-col items-center justify-center">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-500/8 to-transparent" />
          {/* Decorative grid lines */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(oklch(var(--primary-500) / 0.3) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.3) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
          <div className="relative w-16 h-16 flex items-center justify-center border border-primary-500/20 bg-primary-500/5 transition-all duration-500 group-hover:border-primary-500/40 group-hover:bg-primary-500/10 group-hover:shadow-[0_0_16px_oklch(var(--primary-500)_/_0.1)]">
            <i className="ri-search-line text-2xl text-primary-400" />
          </div>
          <p className="relative mt-4 text-xs text-foreground-500 text-center px-6 leading-[1.7]">
            前往崧博出版書籍資料庫確認完整書名、作者、ISBN 與出版日期
          </p>
        </div>

        {/* Info area */}
        <div className="p-5 md:p-6">
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-100 leading-[1.3] tracking-tight">
            查詢更多書目
          </h3>
          <p className="mt-1.5 text-sm text-foreground-300 leading-[1.5]">
            崧博出版書籍資料庫
          </p>
          <div className="mt-4 pt-4 border-t border-primary-500/10">
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-medium text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
            >
              查詢完整書目
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Book2026Section() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScrollEdges();
    el.addEventListener('scroll', checkScrollEdges, { passive: true });
    window.addEventListener('resize', checkScrollEdges);
    return () => {
      el.removeEventListener('scroll', checkScrollEdges);
      window.removeEventListener('resize', checkScrollEdges);
    };
  }, [checkScrollEdges]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 260;
    const gap = 20;
    const scrollAmount = (cardWidth + gap) * 2;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-100 border-t border-primary-500/8"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        {/* Section label */}
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            Latest
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            03.5
          </span>
        </div>

        {/* Section heading */}
        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-100 leading-[1.1] tracking-tight text-center text-glow"
        >
          2026 最新發行
        </h2>

        {/* Section subtitle */}
        <p
          style={getRevealStyle(isVisible, 100)}
          className="mt-5 text-sm md:text-base text-foreground-400 leading-[1.7] max-w-[640px] mx-auto text-center"
        >
          探索機曜文化於 2026 年最新發行的科普與知識類書籍。
        </p>

        <div
          style={getRevealStyle(isVisible, 120)}
          className="mt-6 w-12 h-0.5 bg-primary-500 mx-auto shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/40"
        />

        {/* Horizontal scroll carousel */}
        <div
          style={getRevealStyle(isVisible, 160)}
          className="mt-12 md:mt-16 relative"
        >
          {/* Left arrow */}
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-primary-500/20 bg-background-50/80 backdrop-blur-sm text-primary-400 hover:text-primary-300 hover:border-primary-500/60 hover:bg-background-50/90 transition-all duration-300 cursor-pointer disabled:opacity-0 disabled:cursor-default rounded-full"
            aria-label="向左滑動"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>

          {/* Right arrow */}
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-primary-500/20 bg-background-50/80 backdrop-blur-sm text-primary-400 hover:text-primary-300 hover:border-primary-500/60 hover:bg-background-50/90 transition-all duration-300 cursor-pointer disabled:opacity-0 disabled:cursor-default rounded-full"
            aria-label="向右滑動"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>

          {/* Scrollable track */}
          <div
            ref={scrollRef}
            onScroll={checkScrollEdges}
            className="flex gap-5 overflow-x-auto scrollbar-none px-2 pb-2 -mx-2"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {books2026.map((book) => (
              <div key={book.id} style={{ scrollSnapAlign: 'start' }}>
                <BookCard book={book} />
              </div>
            ))}
            <div style={{ scrollSnapAlign: 'start' }}>
              <CatalogCard />
            </div>
          </div>
        </div>

        {/* Dot indicators */}
        <div
          style={getRevealStyle(isVisible, 200)}
          className="mt-6 flex items-center justify-center gap-2"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500/40" />
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500/20" />
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500/20" />
          <span className="w-1 h-1 rounded-full bg-foreground-500/30" />
          <span className="text-[10px] text-foreground-500 ml-2 font-label tracking-wider">
            {books2026.length + 1} 項 · 滑動瀏覽
          </span>
        </div>

        {/* Catalog reference section — appears once at the bottom */}
        <div
          style={getRevealStyle(isVisible, 260)}
          className="mt-14 md:mt-20 max-w-[780px] mx-auto"
        >
          <div className="border border-primary-500/10 bg-background-50/40 px-6 py-6 md:px-8 md:py-7">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xs text-primary-400 tracking-widest uppercase font-label mb-2">
                  完整書目查詢
                </h3>
                <p className="text-sm text-foreground-300 leading-[1.7] max-w-[480px]">
                  完整書名、作者、內容主題及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
                </p>
              </div>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15 shrink-0"
              >
                前往書籍資料庫
                <i className="ri-arrow-right-up-line text-xs" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
